import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  prompt: string;
  template: string;
  hints?: string[];
  onSubmit: (answer: string) => void;
}

export default function FillBlankExercise({ prompt, template, hints, onSubmit }: Props) {
  const { t } = useTranslation();
  const parts = template.split('___');
  const blankCount = parts.length - 1;
  const [answers, setAnswers] = useState<string[]>(new Array(blankCount).fill(''));

  useEffect(() => {
    setAnswers(new Array(blankCount).fill(''));
  }, [template, blankCount]);

  const updateAnswer = (index: number, value: string) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const handleSubmit = () => {
    let result = '';
    parts.forEach((part, i) => {
      result += part;
      if (i < blankCount) result += answers[i];
    });
    onSubmit(result.trim());
  };

  const allFilled = answers.every(a => a.trim().length > 0);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <p className="text-lg text-gray-800 font-medium">{prompt}</p>
        <p className="text-sm text-gray-400 mt-1">{t('learning.fillHint')}</p>
      </div>

      {hints && hints.length > 0 && (
        <div className="text-center text-sm text-amber-600">
          💡 {hints[0]}
        </div>
      )}

      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-1 text-lg leading-relaxed">
          {parts.map((part, i) => (
            <span key={i} className="flex items-center gap-1">
              <span className="text-gray-700">{part}</span>
              {i < blankCount && (
                <input
                  type="text"
                  value={answers[i]}
                  onChange={e => updateAnswer(i, e.target.value)}
                  className="w-32 px-3 py-1 border-b-2 border-indigo-400 bg-indigo-50 text-indigo-700 font-medium text-center focus:border-indigo-600 outline-none rounded-t"
                  autoFocus={i === 0}
                  placeholder="..."
                />
              )}
            </span>
          ))}
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!allFilled}
        className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl text-lg hover:bg-indigo-700 active:scale-98 transition disabled:opacity-40"
      >
        {t('learning.check')}
      </button>
    </div>
  );
}
